// =============================================================================
// io/sync/exitModels.ts — the user's exit-model catalogue.
// -----------------------------------------------------------------------------
// The twin of `patterns.ts`, and blocked THREE surfaces at once until it landed:
//
//   the trade form's Exit Model picker   — a required field when a pattern is set
//   the Bulk Edit modal's Exit Model row — rendered disabled, saying why
//   Setup Quality's Pattern × Exit-Model — one of the three cards named as missing
//
// ── ⚠ WHY A FAILED READ THROWS ──
//
// Same reasoning as patterns, plus one that is specific to this table and worse.
// The trade form REFUSES TO SAVE a trade that has a pattern and no exit model —
// the old client's rule, because pattern (the entry decision) and exit model (the
// exit execution) travel together and pairing analytics are meaningless with one
// missing. An empty catalogue therefore does not degrade to "no options": it
// degrades to a picker with nothing in it and a save that will not go through,
// with nothing on screen saying the read failed.
//
// A genuinely empty catalogue is a real state — a user who has not written any
// exit models yet — and the form says so in those words. That is the message a
// user can act on, and it must never be shown for a read that simply failed.
//
// ⚠ Deliberately UNPAGED, like patterns: a hand-written catalogue of exit rules
// is a dozen rows, not a thousand. At or above the server cap this throws rather
// than silently truncating, because a truncated catalogue makes some exit models
// unpickable and an unoffered option cannot be noticed as missing.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { PAGE_ROWS } from './load';

/**
 * The whole row. `stop_rule`, `target_rule` and `scale_out_rule` were left out
 * when this loader shipped — "three columns nobody reads, drifting until someone
 * needs them" — and the Exit Model Catalog is that someone: it prints all three
 * on every card and edits them in the modal.
 *
 * ⚠ The rules are the ENTIRE content of an exit model. There is nothing else to
 * fetch, so a "summary" and the full row are the same four fields; the name is
 * kept for continuity with the trade form's picker, which only needs two.
 */
export interface ExitModelSummary {
  id: string;
  name: string;
  stopRule: string;
  targetRule: string;
  scaleOutRule: string;
}

export const EXIT_MODELS_SELECT = 'id,name,stop_rule,target_rule,scale_out_rule';

export function exitModelFromDb(r: Record<string, unknown>): ExitModelSummary {
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  return {
    id: String(r['id'] ?? ''),
    // ⚠ An unnamed exit model still has to be pickable and labelled, or a trade
    // tagged with it renders blank everywhere and looks untagged.
    name: String(r['name'] ?? '') || '(untitled)',
    // ⚠ `''` for a NULL rule, not "No rules". Josh's DISCRETIONARY model has the
    // literal text "No rules" TYPED INTO all three fields — that is his content,
    // and a default with the same words would be indistinguishable from it in the
    // editor while saving something he never wrote.
    stopRule: str(r['stop_rule']),
    targetRule: str(r['target_rule']),
    scaleOutRule: str(r['scale_out_rule']),
  };
}

export async function loadExitModels(sb: SupabaseClient): Promise<ExitModelSummary[]> {
  const { data, error } = await sb
    .from('exit_models')
    .select(EXIT_MODELS_SELECT)
    .order('name', { ascending: true });

  if (error) {
    throw new Error(
      `loadExitModels: ${error.message}. Refusing to continue with an empty catalogue — ` +
        `the trade form blocks a save when a pattern is set and no exit model is, so an ` +
        `empty picker reads as "you have not written any exit models" when the truth is ` +
        `that the read failed.`,
    );
  }

  const rows = (data ?? []) as unknown as Record<string, unknown>[];
  if (rows.length >= PAGE_ROWS) {
    throw new Error(
      `loadExitModels: got ${rows.length} rows, at or above the ${PAGE_ROWS}-row server cap. ` +
        `This read is unpaged because an exit-model catalogue is hand-written; at this size ` +
        `it is being truncated and some exit models would be unpickable.`,
    );
  }

  return rows.map(exitModelFromDb);
}
